import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Upload, Plus, Edit, Trash2, LogOut, ArrowLeft,
  FileText, ImageIcon, Check, Loader2
} from "lucide-react";
import { getApiUrl } from "@/lib/api";

const API = getApiUrl();

const CATEGORIES = [
  "Předkrmy", "Polévky", "Tradiční česká jídla", "Burgery", "Těstoviny",
  "Tortily", "Ukrajinské speciality", "Klasika z českého grilu", "Saláty",
  "Přílohy", "Omáčky", "Aperitivy", "Likéry a destiláty", "Brandy a koňak",
  "Whisky a bourbon", "Pivo", "Nealkoholické nápoje", "Energetické nápoje",
  "Teplé nápoje", "Víno a sekt", "Doplňkový prodej"
];
const BREAKFAST_CATEGORIES = ["Snídaně", "Nápoje ke snídani"];

function getHeaders() {
  const token = localStorage.getItem("admin_token");
  return { Authorization: `Bearer ${token}` };
}

/* ──── Weekly Menu Tab ──── */
function WeeklyMenuTab() {
  const [menuInfo, setMenuInfo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchMenu = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/weekly-menu`);
      if (res.data?.filename) setMenuInfo(res.data);
      else setMenuInfo(null);
    } catch { setMenuInfo(null); }
  }, []);

  useEffect(() => { fetchMenu(); }, [fetchMenu]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      const res = await axios.post(`${API}/weekly-menu/upload`, formData, {
        headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
      });
      setMenuInfo(res.data);
      toast.success("Menu úspěšně nahráno");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Chyba při nahrávání");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/weekly-menu`, { headers: getHeaders() });
      setMenuInfo(null);
      toast.success("Menu smazáno");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Chyba při mazání");
    } finally {
      setDeleting(false);
    }
  };

  const fileUrl = menuInfo ? `${API}/weekly-menu/file/${menuInfo.filename}` : null;
  const isImage = menuInfo?.file_type?.startsWith("image/");

  return (
    <div className="space-y-6" data-testid="weekly-menu-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-['Playfair_Display'] text-xl text-[#F0FDF4]">Polední menu</h3>
        <div className="flex gap-2">
          {menuInfo && (
            <Button
              onClick={handleDelete}
              disabled={deleting}
              variant="outline"
              className="border-red-500/50 text-red-400 hover:bg-red-900/20 hover:text-red-300 rounded-sm font-semibold uppercase tracking-wider px-4 py-2 text-sm"
              data-testid="delete-weekly-menu-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Smazat
            </Button>
          )}
          <label
            className="cursor-pointer inline-flex items-center gap-2 bg-[#EAB308] text-[#02120D] hover:bg-yellow-500 rounded-sm font-semibold uppercase tracking-wider px-4 py-2 text-sm transition-colors"
            data-testid="upload-menu-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {menuInfo ? "Nahradit soubor" : "Nahrát menu"}
            <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.bmp,.tiff,.tif,.heic,.heif" onChange={handleUpload} />
          </label>
        </div>
      </div>

      {menuInfo ? (
        <div className="border border-[#1D4133] rounded-sm overflow-hidden bg-[#02120D]/50">
          <div className="p-4 flex items-center gap-3 border-b border-[#1D4133]/50">
            {isImage ? <ImageIcon className="w-5 h-5 text-[#EAB308]" /> : <FileText className="w-5 h-5 text-[#EAB308]" />}
            <div>
              <p className="text-sm text-[#F0FDF4]">{menuInfo.original_name}</p>
              <p className="text-xs text-[#94A3B8]">
                Nahráno: {new Date(menuInfo.upload_date).toLocaleDateString("cs-CZ")}
              </p>
            </div>
            <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="ml-auto text-xs text-[#EAB308] hover:underline">
              Otevřít
            </a>
          </div>
          {isImage && (
            <img src={fileUrl} alt="Nahled menu" className="w-full max-h-[400px] object-contain p-4" />
          )}
        </div>
      ) : (
        <div className="border border-dashed border-[#1D4133] rounded-sm p-12 text-center">
          <Upload className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
          <p className="text-[#94A3B8]">Zatím není nahráno žádné menu</p>
          <p className="text-xs text-[#94A3B8]/60 mt-1">Podporované formáty: PDF, JPG, PNG, WEBP, GIF, BMP, TIFF, HEIC</p>
        </div>
      )}
    </div>
  );
}

/* ──── Evening Menu Tab ──── */
function EveningMenuTab() {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ category: "Předkrmy", name: "", description: "", price: "", allergens: "", sort_order: 0 });

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/evening-menu`);
      setItems(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const resetForm = () => {
    setForm({ category: "Předkrmy", name: "", description: "", price: "", allergens: "", sort_order: 0 });
    setEditing(null);
  };

  const openAdd = () => { resetForm(); setDialogOpen(true); };
  const openEdit = (item) => {
    setForm({ category: item.category, name: item.name, description: item.description || "", price: item.price, allergens: item.allergens || "", sort_order: item.sort_order || 0 });
    setEditing(item.id);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.price) { toast.error("Vyplňte název a cenu"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/evening-menu/${editing}`, form, { headers: getHeaders() });
        toast.success("Položka aktualizována");
      } else {
        await axios.post(`${API}/evening-menu`, form, { headers: getHeaders() });
        toast.success("Položka přidána");
      }
      setDialogOpen(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Chyba");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/evening-menu/${id}`, { headers: getHeaders() });
      toast.success("Položka smazána");
      fetchItems();
    } catch { toast.error("Chyba při mazání"); }
  };

  const categories = [...new Set(items.map((i) => i.category))];

  return (
    <div className="space-y-6" data-testid="evening-menu-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-['Playfair_Display'] text-xl text-[#F0FDF4]">Večerní menu</h3>
        <Button
          onClick={openAdd}
          data-testid="add-menu-item-btn"
          className="bg-[#EAB308] text-[#02120D] hover:bg-yellow-500 rounded-sm font-semibold uppercase tracking-wider px-4 py-2 text-sm"
        >
          <Plus className="w-4 h-4 mr-2" /> Přidat
        </Button>
      </div>

      {categories.length === 0 ? (
        <p className="text-[#94A3B8] text-center py-8">Zatím žádné položky</p>
      ) : (
        categories.map((cat) => (
          <div key={cat}>
            <h4 className="text-[#EAB308] font-semibold text-sm uppercase tracking-wider mb-3">{cat}</h4>
            <div className="space-y-2">
              {items.filter((i) => i.category === cat).map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 bg-[#02120D]/50 border border-[#1D4133]/50 rounded-sm p-3"
                  data-testid={`admin-item-${item.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-[#F0FDF4] text-sm font-medium truncate">{item.name}</span>
                      <span className="text-[#EAB308] text-sm font-bold whitespace-nowrap">{item.price}</span>
                    </div>
                    {item.description && <p className="text-xs text-[#94A3B8] truncate">{item.description}</p>}
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(item)} className="text-[#94A3B8] hover:text-[#EAB308] hover:bg-[#0B2A20]" data-testid={`edit-item-${item.id}`}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} className="text-[#94A3B8] hover:text-red-400 hover:bg-red-900/20" data-testid={`delete-item-${item.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <Separator className="mt-4 bg-[#1D4133]/30" />
          </div>
        ))
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#051F16] border-[#1D4133] text-[#F0FDF4] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-lg">
              {editing ? "Upravit položku" : "Nová položka"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#E2E8F0] text-sm">Kategorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-[#0B2A20] border-[#1D4133] text-[#F0FDF4] rounded-sm" data-testid="select-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#051F16] border-[#1D4133]">
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c} className="text-[#F0FDF4] focus:bg-[#0B2A20] focus:text-[#F0FDF4]">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#E2E8F0] text-sm">Název</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-[#0B2A20] border-[#1D4133] text-[#F0FDF4] rounded-sm"
                data-testid="input-item-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#E2E8F0] text-sm">Popis</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-[#0B2A20] border-[#1D4133] text-[#F0FDF4] rounded-sm"
                data-testid="input-item-desc"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#E2E8F0] text-sm">Cena</Label>
                <Input
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  className="bg-[#0B2A20] border-[#1D4133] text-[#F0FDF4] rounded-sm"
                  placeholder="189 Kč"
                  data-testid="input-item-price"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#E2E8F0] text-sm">Alergeny</Label>
                <Input
                  value={form.allergens}
                  onChange={(e) => setForm({ ...form, allergens: e.target.value })}
                  className="bg-[#0B2A20] border-[#1D4133] text-[#F0FDF4] rounded-sm"
                  placeholder="1, 3, 7"
                  data-testid="input-item-allergens"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#E2E8F0] text-sm">Pořadí</Label>
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                className="bg-[#0B2A20] border-[#1D4133] text-[#F0FDF4] rounded-sm"
                data-testid="input-item-order"
              />
            </div>
            <Button
              onClick={handleSave}
              data-testid="save-item-btn"
              className="w-full bg-[#EAB308] text-[#02120D] hover:bg-yellow-500 rounded-sm font-semibold uppercase tracking-wider"
            >
              <Check className="w-4 h-4 mr-2" />
              {editing ? "Uložit změny" : "Přidat položku"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ──── Breakfast Menu Tab ──── */
function BreakfastMenuTab() {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ category: "Snídaně", name: "", description: "", price: "", allergens: "", sort_order: 0 });

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/breakfast-menu`);
      setItems(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const resetForm = () => {
    setForm({ category: "Snídaně", name: "", description: "", price: "", allergens: "", sort_order: 0 });
    setEditing(null);
  };

  const openAdd = () => { resetForm(); setDialogOpen(true); };
  const openEdit = (item) => {
    setForm({ category: item.category, name: item.name, description: item.description || "", price: item.price, allergens: item.allergens || "", sort_order: item.sort_order || 0 });
    setEditing(item.id);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.price) { toast.error("Vyplňte název a cenu"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/breakfast-menu/${editing}`, form, { headers: getHeaders() });
        toast.success("Položka aktualizována");
      } else {
        await axios.post(`${API}/breakfast-menu`, form, { headers: getHeaders() });
        toast.success("Položka přidána");
      }
      setDialogOpen(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Chyba");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/breakfast-menu/${id}`, { headers: getHeaders() });
      toast.success("Položka smazána");
      fetchItems();
    } catch { toast.error("Chyba při mazání"); }
  };

  const categories = [...new Set(items.map((i) => i.category))];

  return (
    <div className="space-y-6" data-testid="breakfast-menu-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-['Playfair_Display'] text-xl text-[#F0FDF4]">Snídaně</h3>
        <Button
          onClick={openAdd}
          data-testid="add-breakfast-item-btn"
          className="bg-[#EAB308] text-[#02120D] hover:bg-yellow-500 rounded-sm font-semibold uppercase tracking-wider px-4 py-2 text-sm"
        >
          <Plus className="w-4 h-4 mr-2" /> Přidat
        </Button>
      </div>

      {categories.length === 0 ? (
        <p className="text-[#94A3B8] text-center py-8">Zatím žádné položky</p>
      ) : (
        categories.map((cat) => (
          <div key={cat}>
            <h4 className="text-[#EAB308] font-semibold text-sm uppercase tracking-wider mb-3">{cat}</h4>
            <div className="space-y-2">
              {items.filter((i) => i.category === cat).map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 bg-[#02120D]/50 border border-[#1D4133]/50 rounded-sm p-3"
                  data-testid={`admin-breakfast-${item.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-[#F0FDF4] text-sm font-medium truncate">{item.name}</span>
                      <span className="text-[#EAB308] text-sm font-bold whitespace-nowrap">{item.price}</span>
                    </div>
                    {item.description && <p className="text-xs text-[#94A3B8] truncate">{item.description}</p>}
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(item)} className="text-[#94A3B8] hover:text-[#EAB308] hover:bg-[#0B2A20]">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)} className="text-[#94A3B8] hover:text-red-400 hover:bg-red-900/20">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <Separator className="mt-4 bg-[#1D4133]/30" />
          </div>
        ))
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#051F16] border-[#1D4133] text-[#F0FDF4] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-lg">
              {editing ? "Upravit položku" : "Nová položka"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#E2E8F0] text-sm">Kategorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-[#0B2A20] border-[#1D4133] text-[#F0FDF4] rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#051F16] border-[#1D4133]">
                  {BREAKFAST_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c} className="text-[#F0FDF4] focus:bg-[#0B2A20] focus:text-[#F0FDF4]">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#E2E8F0] text-sm">Název</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0B2A20] border-[#1D4133] text-[#F0FDF4] rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#E2E8F0] text-sm">Popis</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#0B2A20] border-[#1D4133] text-[#F0FDF4] rounded-sm" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#E2E8F0] text-sm">Cena</Label>
                <Input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-[#0B2A20] border-[#1D4133] text-[#F0FDF4] rounded-sm" placeholder="89 Kč" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#E2E8F0] text-sm">Alergeny</Label>
                <Input value={form.allergens} onChange={(e) => setForm({ ...form, allergens: e.target.value })} className="bg-[#0B2A20] border-[#1D4133] text-[#F0FDF4] rounded-sm" placeholder="1, 3, 7" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#E2E8F0] text-sm">Pořadí</Label>
              <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} className="bg-[#0B2A20] border-[#1D4133] text-[#F0FDF4] rounded-sm" />
            </div>
            <Button onClick={handleSave} className="w-full bg-[#EAB308] text-[#02120D] hover:bg-yellow-500 rounded-sm font-semibold uppercase tracking-wider">
              <Check className="w-4 h-4 mr-2" />
              {editing ? "Uložit změny" : "Přidat položku"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ──── Holiday Menu Tab ──── */
function HolidayMenuTab() {
  const [menuInfo, setMenuInfo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchMenu = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/holiday-menu`);
      if (res.data?.filename) setMenuInfo(res.data);
      else setMenuInfo(null);
    } catch { setMenuInfo(null); }
  }, []);

  useEffect(() => { fetchMenu(); }, [fetchMenu]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      const res = await axios.post(`${API}/holiday-menu/upload`, formData, {
        headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
      });
      setMenuInfo(res.data);
      toast.success("Sváteční menu úspěšně nahráno");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Chyba při nahrávání");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/holiday-menu`, { headers: getHeaders() });
      setMenuInfo(null);
      toast.success("Sváteční menu smazáno");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Chyba při mazání");
    } finally {
      setDeleting(false);
    }
  };

  const fileUrl = menuInfo ? `${API}/holiday-menu/file/${menuInfo.filename}` : null;
  const isImage = menuInfo?.file_type?.startsWith("image/");

  return (
    <div className="space-y-6" data-testid="holiday-menu-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-['Playfair_Display'] text-xl text-[#F0FDF4]">Sváteční menu</h3>
        <div className="flex gap-2">
          {menuInfo && (
            <Button
              onClick={handleDelete}
              disabled={deleting}
              variant="outline"
              className="border-red-500/50 text-red-400 hover:bg-red-900/20 hover:text-red-300 rounded-sm font-semibold uppercase tracking-wider px-4 py-2 text-sm"
              data-testid="delete-holiday-menu-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Smazat
            </Button>
          )}
          <label
            className="cursor-pointer inline-flex items-center gap-2 bg-[#EAB308] text-[#02120D] hover:bg-yellow-500 rounded-sm font-semibold uppercase tracking-wider px-4 py-2 text-sm transition-colors"
            data-testid="upload-holiday-menu-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {menuInfo ? "Nahradit soubor" : "Nahrát menu"}
            <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.bmp,.tiff,.tif,.heic,.heif" onChange={handleUpload} />
          </label>
        </div>
      </div>

      {menuInfo ? (
        <div className="border border-[#1D4133] rounded-sm overflow-hidden bg-[#02120D]/50">
          <div className="p-4 flex items-center gap-3 border-b border-[#1D4133]/50">
            {isImage ? <ImageIcon className="w-5 h-5 text-[#EAB308]" /> : <FileText className="w-5 h-5 text-[#EAB308]" />}
            <div>
              <p className="text-sm text-[#F0FDF4]">{menuInfo.original_name}</p>
              <p className="text-xs text-[#94A3B8]">
                Nahráno: {new Date(menuInfo.upload_date).toLocaleDateString("cs-CZ")}
              </p>
            </div>
            <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="ml-auto text-xs text-[#EAB308] hover:underline">
              Otevřít
            </a>
          </div>
          {isImage && (
            <img src={fileUrl} alt="Nahled sváteční menu" className="w-full max-h-[400px] object-contain p-4" />
          )}
        </div>
      ) : (
        <div className="border border-dashed border-[#1D4133] rounded-sm p-12 text-center">
          <Upload className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
          <p className="text-[#94A3B8]">Zatím není nahráno žádné sváteční menu</p>
          <p className="text-xs text-[#94A3B8]/60 mt-1">Podporované formáty: PDF, JPG, PNG, WEBP, GIF, BMP, TIFF, HEIC</p>
        </div>
      )}
    </div>
  );
}

/* ──── Settings Tab ──── */
function SettingsTab() {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = async (e) => {
    e.preventDefault();
    if (!currentPw || !newPw) return;
    setLoading(true);
    try {
      await axios.put(`${API}/auth/change-password`, { current_password: currentPw, new_password: newPw }, { headers: getHeaders() });
      toast.success("Heslo změněno");
      setCurrentPw("");
      setNewPw("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Chyba");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md space-y-6" data-testid="settings-tab">
      <h3 className="font-['Playfair_Display'] text-xl text-[#F0FDF4]">Změna hesla</h3>
      <form onSubmit={handleChange} className="space-y-4">
        <div className="space-y-2">
          <Label className="text-[#E2E8F0] text-sm">Aktuální heslo</Label>
          <Input
            type="password"
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            className="bg-[#0B2A20] border-[#1D4133] text-[#F0FDF4] rounded-sm"
            data-testid="current-password-input"
            required
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[#E2E8F0] text-sm">Nové heslo</Label>
          <Input
            type="password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            className="bg-[#0B2A20] border-[#1D4133] text-[#F0FDF4] rounded-sm"
            data-testid="new-password-input"
            required
          />
        </div>
        <Button
          type="submit"
          disabled={loading}
          data-testid="change-password-btn"
          className="bg-[#EAB308] text-[#02120D] hover:bg-yellow-500 rounded-sm font-semibold uppercase tracking-wider"
        >
          {loading ? "Měním..." : "Změnit heslo"}
        </Button>
      </form>
    </div>
  );
}

/* ──── Main Dashboard ──── */
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) { navigate("/admin/login"); return; }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setUser(res.data))
      .catch(() => { localStorage.removeItem("admin_token"); navigate("/admin/login"); });
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_username");
    navigate("/admin/login");
  };

  if (!user) return (
    <div className="min-h-screen bg-[#02120D] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-[#EAB308] animate-spin" />
    </div>
  );

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#02120D]">
      {/* Top bar */}
      <div className="bg-[#051F16] border-b border-[#1D4133]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <div className="flex items-center gap-4">
            <a href="/" className="text-[#94A3B8] hover:text-[#EAB308] transition-colors" data-testid="admin-back-home">
              <ArrowLeft className="w-5 h-5" />
            </a>
            <span className="font-['Playfair_Display'] text-lg font-bold text-[#F0FDF4]">
              Administrace
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#94A3B8]">{user.username}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-[#94A3B8] hover:text-red-400 hover:bg-red-900/20"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" /> Odhlásit
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="weekly" className="space-y-6">
          <TabsList className="bg-[#051F16] border border-[#1D4133] rounded-sm p-1 flex-wrap">
            <TabsTrigger
              value="weekly"
              data-testid="tab-weekly"
              className="data-[state=active]:bg-[#0B2A20] data-[state=active]:text-[#EAB308] text-[#94A3B8] rounded-sm"
            >
              Polední menu
            </TabsTrigger>
            <TabsTrigger
              value="holiday"
              data-testid="tab-holiday"
              className="data-[state=active]:bg-[#0B2A20] data-[state=active]:text-[#EAB308] text-[#94A3B8] rounded-sm"
            >
              Sváteční menu
            </TabsTrigger>
            <TabsTrigger
              value="breakfast"
              data-testid="tab-breakfast"
              className="data-[state=active]:bg-[#0B2A20] data-[state=active]:text-[#EAB308] text-[#94A3B8] rounded-sm"
            >
              Snídaně
            </TabsTrigger>
            <TabsTrigger
              value="evening"
              data-testid="tab-evening"
              className="data-[state=active]:bg-[#0B2A20] data-[state=active]:text-[#EAB308] text-[#94A3B8] rounded-sm"
            >
              Večerní menu
            </TabsTrigger>
            <TabsTrigger
              value="settings"
              data-testid="tab-settings"
              className="data-[state=active]:bg-[#0B2A20] data-[state=active]:text-[#EAB308] text-[#94A3B8] rounded-sm"
            >
              Nastavení
            </TabsTrigger>
          </TabsList>

          <TabsContent value="weekly"><WeeklyMenuTab /></TabsContent>
          <TabsContent value="holiday"><HolidayMenuTab /></TabsContent>
          <TabsContent value="breakfast"><BreakfastMenuTab /></TabsContent>
          <TabsContent value="evening"><EveningMenuTab /></TabsContent>
          <TabsContent value="settings"><SettingsTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
